import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath, localize2WithPath } from 'vscode/vscode/vs/nls';
import { KeyChord } from 'vscode/vscode/vs/base/common/keyCodes';
import { ModesRegistry } from 'vscode/vscode/vs/editor/common/languages/modesRegistry';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { registerAction2, Action2, MenuId, MenuRegistry } from 'vscode/vscode/vs/platform/actions/common/actions';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import './outputServices.js';
import { OUTPUT_MODE_ID, OUTPUT_MIME, LOG_MODE_ID, LOG_MIME, OUTPUT_VIEW_ID, ACTIVE_OUTPUT_CHANNEL_CONTEXT, Extensions as Extensions$1, CONTEXT_IN_OUTPUT, CONTEXT_OUTPUT_SCROLL_LOCK, CONTEXT_ACTIVE_FILE_OUTPUT, CONTEXT_ACTIVE_OUTPUT_LEVEL_SETTABLE, CONTEXT_ACTIVE_OUTPUT_LEVEL, CONTEXT_ACTIVE_OUTPUT_LEVEL_IS_DEFAULT } from 'vscode/vscode/vs/workbench/services/output/common/output';
import { IOutputService } from 'vscode/vscode/vs/workbench/services/output/common/output.service';
import { OutputViewPane } from './outputView.js';
import { Extensions as Extensions$2 } from 'vscode/vscode/vs/workbench/common/contributions';
import { Extensions } from 'vscode/vscode/vs/workbench/common/views';
import { IViewsService } from 'vscode/vscode/vs/workbench/services/views/common/viewsService.service';
import { ViewPaneContainer } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPaneContainer';
import { Extensions as Extensions$3 } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { IQuickInputService } from 'vscode/vscode/vs/platform/quickinput/common/quickInput.service';
import { AUX_WINDOW_GROUP } from 'vscode/vscode/vs/workbench/services/editor/common/editorService';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { assertIsDefined } from 'vscode/vscode/vs/base/common/types';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { registerIcon } from 'vscode/vscode/vs/platform/theme/common/iconRegistry';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';
import { Disposable, toDisposable, dispose } from 'vscode/vscode/vs/base/common/lifecycle';
import { IFilesConfigurationService } from 'vscode/vscode/vs/workbench/services/filesConfiguration/common/filesConfigurationService.service';
import { AccessibilitySignal } from 'vscode/vscode/vs/platform/accessibilitySignal/browser/accessibilitySignalService';
import { IAccessibilitySignalService } from 'vscode/vscode/vs/platform/accessibilitySignal/browser/accessibilitySignalService.service';
import { LogLevelToLocalizedString, LogLevelToString, LogLevel } from 'vscode/vscode/vs/platform/log/common/log';
import { ILoggerService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { IDefaultLogLevelsService } from 'vscode/vscode/vs/workbench/contrib/logs/common/defaultLogLevels.service';

const _moduleId = "vs/workbench/contrib/output/browser/output.contribution";
ModesRegistry.registerLanguage({
    id: OUTPUT_MODE_ID,
    extensions: [],
    mimetypes: [OUTPUT_MIME]
});
ModesRegistry.registerLanguage({
    id: LOG_MODE_ID,
    extensions: [],
    mimetypes: [LOG_MIME]
});
const outputViewIcon = registerIcon('output-view-icon', Codicon.output, ( localizeWithPath(_moduleId, 0, 'View icon of the output view.')));
const VIEW_CONTAINER = ( (Registry.as(Extensions.ViewContainersRegistry))).registerViewContainer({
    id: OUTPUT_VIEW_ID,
    title: ( localize2WithPath(_moduleId, 1, "Output")),
    icon: outputViewIcon,
    order: 1,
    ctorDescriptor: ( (new SyncDescriptor(
        ViewPaneContainer,
        [OUTPUT_VIEW_ID, { mergeViewWithContainerWhenSingleView: true }]
    ))),
    storageId: OUTPUT_VIEW_ID,
    hideIfEmpty: true,
}, 1 , { doNotRegisterOpenCommand: true });
( (Registry.as(Extensions.ViewsRegistry))).registerViews([{
        id: OUTPUT_VIEW_ID,
        name: ( localize2WithPath(_moduleId, 1, "Output")),
        containerIcon: outputViewIcon,
        canMoveView: true,
        canToggleVisibility: false,
        ctorDescriptor: ( (new SyncDescriptor(OutputViewPane))),
        openCommandActionDescriptor: {
            id: 'workbench.action.output.toggleOutput',
            mnemonicTitle: ( localizeWithPath(_moduleId, 2, "&&Output")),
            keybindings: {
                primary: 2048  | 1024  | 51 ,
                linux: {
                    primary: KeyChord(2048  | 41 , 2048  | 38 )
                }
            },
            order: 1,
        }
    }], VIEW_CONTAINER);
let OutputContribution = class OutputContribution extends Disposable {
    constructor(outputService, editorService, fileConfigurationService) {
        super();
        this.outputService = outputService;
        this.editorService = editorService;
        this.fileConfigurationService = fileConfigurationService;
        this.registerActions();
    }
    registerActions() {
        this.registerSwitchOutputAction();
        this.registerShowOutputChannelsAction();
        this.registerClearOutputAction();
        this.registerToggleAutoScrollAction();
        this.registerOpenActiveOutputFileAction();
        this.registerOpenActiveOutputFileInAuxWindowAction();
        this.registerShowLogsAction();
        this.registerOpenLogFileAction();
        this.registerConfigureActiveOutputLogLevelAction();
    }
    registerSwitchOutputAction() {
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: `workbench.output.action.switchBetweenOutputs`,
                    title: ( localizeWithPath(_moduleId, 3, "Switch Output")),
                });
            }
            async run(accessor, channelId) {
                if (channelId) {
                    accessor.get(IOutputService).showChannel(channelId, true);
                }
            }
        }));
        const switchOutputMenu = ( (new MenuId('workbench.output.menu.switchOutput')));
        this._register(MenuRegistry.appendMenuItem(MenuId.ViewTitle, {
            submenu: switchOutputMenu,
            title: ( localizeWithPath(_moduleId, 4, "Switch Output")),
            group: 'navigation',
            when: ( (ContextKeyExpr.equals('view', OUTPUT_VIEW_ID))),
            order: 1,
            isSelection: true
        }));
        const registeredChannels = ( (new Map()));
        this._register(toDisposable(() => dispose(( (registeredChannels.values())))));
        const registerOutputChannels = (channels) => {
            for (const channel of channels) {
                const title = channel.label;
                const group = channel.extensionId ? '0_ext_outputchannels' : '1_core_outputchannels';
                registeredChannels.set(channel.id, registerAction2(class extends Action2 {
                    constructor() {
                        super({
                            id: `workbench.action.output.show.${channel.id}`,
                            title,
                            toggled: ( (ACTIVE_OUTPUT_CHANNEL_CONTEXT.isEqualTo(channel.id))),
                            menu: {
                                id: switchOutputMenu,
                                group,
                            }
                        });
                    }
                    async run(accessor) {
                        return accessor.get(IOutputService).showChannel(channel.id, true);
                    }
                }));
            }
        };
        registerOutputChannels(this.outputService.getChannelDescriptors());
        const outputChannelRegistry = ( (Registry.as(Extensions$1.OutputChannels)));
        this._register(outputChannelRegistry.onDidRegisterChannel(e => {
            const channel = this.outputService.getChannelDescriptor(e);
            if (channel) {
                registerOutputChannels([channel]);
            }
        }));
        this._register(outputChannelRegistry.onDidRemoveChannel(e => {
            registeredChannels.get(e)?.dispose();
            registeredChannels.delete(e);
        }));
    }
    registerShowOutputChannelsAction() {
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: 'workbench.action.showOutputChannels',
                    title: ( localize2WithPath(_moduleId, 5, "Show Output Channels...")),
                    category: ( localize2WithPath(_moduleId, 1, "Output")),
                    f1: true
                });
            }
            async run(accessor) {
                const outputService = accessor.get(IOutputService);
                const quickInputService = accessor.get(IQuickInputService);
                const extensionChannels = [], coreChannels = [];
                for (const channel of outputService.getChannelDescriptors()) {
                    if (channel.extensionId) {
                        extensionChannels.push(channel);
                    }
                    else {
                        coreChannels.push(channel);
                    }
                }
                const entries = [];
                for (const { id, label } of extensionChannels) {
                    entries.push({ id, label });
                }
                if (extensionChannels.length && coreChannels.length) {
                    entries.push({ type: 'separator' });
                }
                for (const { id, label } of coreChannels) {
                    entries.push({ id, label });
                }
                const entry = await quickInputService.pick(entries, { placeHolder: ( localizeWithPath(_moduleId, 6, "Select Output Channel")) });
                if (entry) {
                    return outputService.showChannel(entry.id);
                }
            }
        }));
    }
    registerClearOutputAction() {
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: `workbench.output.action.clearOutput`,
                    title: ( localize2WithPath(_moduleId, 7, "Clear Output")),
                    category: Categories.View,
                    menu: [{
                            id: MenuId.ViewTitle,
                            when: ( (ContextKeyExpr.equals('view', OUTPUT_VIEW_ID))),
                            group: 'navigation',
                            order: 2
                        }, {
                            id: MenuId.CommandPalette
                        }, {
                            id: MenuId.EditorContext,
                            when: CONTEXT_IN_OUTPUT
                        }],
                    icon: Codicon.clearAll
                });
            }
            async run(accessor) {
                const outputService = accessor.get(IOutputService);
                const accessibilitySignalService = accessor.get(IAccessibilitySignalService);
                const activeChannel = outputService.getActiveChannel();
                if (activeChannel) {
                    activeChannel.clear();
                    accessibilitySignalService.playSignal(AccessibilitySignal.clear);
                }
            }
        }));
    }
    registerToggleAutoScrollAction() {
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: `workbench.output.action.toggleAutoScroll`,
                    title: ( localize2WithPath(_moduleId, 8, "Toggle Auto Scrolling")),
                    tooltip: ( localizeWithPath(_moduleId, 9, "Turn Auto Scrolling Off")),
                    menu: {
                        id: MenuId.ViewTitle,
                        when: ( (ContextKeyExpr.and( (ContextKeyExpr.equals('view', OUTPUT_VIEW_ID))))),
                        group: 'navigation',
                        order: 3,
                    },
                    icon: Codicon.lock,
                    toggled: {
                        condition: CONTEXT_OUTPUT_SCROLL_LOCK,
                        icon: Codicon.unlock,
                        tooltip: ( localizeWithPath(_moduleId, 10, "Turn Auto Scrolling On"))
                    }
                });
            }
            async run(accessor) {
                const outputView = accessor.get(IViewsService).getActiveViewWithId(OUTPUT_VIEW_ID);
                outputView.scrollLock = !outputView.scrollLock;
            }
        }));
    }
    registerOpenActiveOutputFileAction() {
        const that = this;
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: `workbench.action.openActiveLogOutputFile`,
                    title: ( localize2WithPath(_moduleId, 11, "Open Output in Editor")),
                    menu: [{
                            id: MenuId.ViewTitle,
                            when: ( (ContextKeyExpr.equals('view', OUTPUT_VIEW_ID))),
                            group: 'navigation',
                            order: 4,
                            isHiddenByDefault: true
                        }],
                    icon: Codicon.goToFile,
                    precondition: CONTEXT_ACTIVE_FILE_OUTPUT
                });
            }
            async run() {
                that.openActiveOutoutFile();
            }
        }));
    }
    registerOpenActiveOutputFileInAuxWindowAction() {
        const that = this;
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: `workbench.action.openActiveLogOutputFileInNewWindow`,
                    title: ( localize2WithPath(_moduleId, 12, "Open Output in New Window")),
                    menu: [{
                            id: MenuId.ViewTitle,
                            when: ( (ContextKeyExpr.equals('view', OUTPUT_VIEW_ID))),
                            group: 'navigation',
                            order: 5,
                            isHiddenByDefault: true
                        }],
                    icon: Codicon.emptyWindow,
                    precondition: CONTEXT_ACTIVE_FILE_OUTPUT
                });
            }
            async run() {
                that.openActiveOutoutFile(AUX_WINDOW_GROUP);
            }
        }));
    }
    async openActiveOutoutFile(group) {
        const fileOutputChannelDescriptor = this.getFileOutputChannelDescriptor();
        if (fileOutputChannelDescriptor) {
            await this.fileConfigurationService.updateReadonly(fileOutputChannelDescriptor.file, true);
            await this.editorService.openEditor({
                resource: fileOutputChannelDescriptor.file,
                options: {
                    pinned: true,
                },
            }, group);
        }
    }
    getFileOutputChannelDescriptor() {
        const channel = this.outputService.getActiveChannel();
        if (channel) {
            const descriptor = this.outputService.getChannelDescriptors().filter(c => c.id === channel.id)[0];
            if (descriptor?.file) {
                return descriptor;
            }
        }
        return null;
    }
    registerConfigureActiveOutputLogLevelAction() {
        const that = this;
        const logLevelMenu = ( (new MenuId('workbench.output.menu.logLevel')));
        this._register(MenuRegistry.appendMenuItem(MenuId.ViewTitle, {
            submenu: logLevelMenu,
            title: ( localizeWithPath(_moduleId, 13, "Set Log Level...")),
            group: 'navigation',
            when: ( (ContextKeyExpr.and(
                 (ContextKeyExpr.equals('view', OUTPUT_VIEW_ID)),
                CONTEXT_ACTIVE_OUTPUT_LEVEL_SETTABLE
            ))),
            icon: Codicon.gear,
            order: 6
        }));
        let order = 0;
        const registerLogLevel = (logLevel) => {
            this._register(registerAction2(class extends Action2 {
                constructor() {
                    super({
                        id: `workbench.action.output.activeOutputLogLevel.${logLevel}`,
                        title: LogLevelToLocalizedString(logLevel).value,
                        toggled: ( (CONTEXT_ACTIVE_OUTPUT_LEVEL.isEqualTo(LogLevelToString(logLevel)))),
                        menu: {
                            id: logLevelMenu,
                            order: order++,
                            group: '0_level'
                        }
                    });
                }
                async run(accessor) {
                    const channel = that.outputService.getActiveChannel();
                    if (channel) {
                        const channelDescriptor = that.outputService.getChannelDescriptor(channel.id);
                        if (channelDescriptor?.log && channelDescriptor.file) {
                            return accessor.get(ILoggerService).setLogLevel(channelDescriptor.file, logLevel);
                        }
                    }
                }
            }));
        };
        registerLogLevel(LogLevel.Trace);
        registerLogLevel(LogLevel.Debug);
        registerLogLevel(LogLevel.Info);
        registerLogLevel(LogLevel.Warning);
        registerLogLevel(LogLevel.Error);
        registerLogLevel(LogLevel.Off);
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: `workbench.action.output.activeOutputLogLevelDefault`,
                    title: ( localizeWithPath(_moduleId, 14, "Set As Default")),
                    menu: {
                        id: logLevelMenu,
                        order,
                        group: '1_default'
                    },
                    precondition: ( (CONTEXT_ACTIVE_OUTPUT_LEVEL_IS_DEFAULT.negate()))
                });
            }
            async run(accessor) {
                const channel = that.outputService.getActiveChannel();
                if (channel) {
                    const channelDescriptor = that.outputService.getChannelDescriptor(channel.id);
                    if (channelDescriptor?.log && channelDescriptor.file) {
                        const logLevel = accessor.get(ILoggerService).getLogLevel(channelDescriptor.file);
                        return await accessor.get(IDefaultLogLevelsService).setDefaultLogLevel(logLevel, channelDescriptor.extensionId);
                    }
                }
            }
        }));
    }
    registerShowLogsAction() {
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: 'workbench.action.showLogs',
                    title: ( localize2WithPath(_moduleId, 15, "Show Logs...")),
                    category: Categories.Developer,
                    menu: {
                        id: MenuId.CommandPalette,
                    },
                });
            }
            async run(accessor) {
                const outputService = accessor.get(IOutputService);
                const quickInputService = accessor.get(IQuickInputService);
                const extensionLogs = [], logs = [];
                for (const channel of outputService.getChannelDescriptors()) {
                    if (channel.log) {
                        if (channel.extensionId) {
                            extensionLogs.push(channel);
                        }
                        else {
                            logs.push(channel);
                        }
                    }
                }
                const entries = [];
                for (const { id, label } of logs) {
                    entries.push({ id, label });
                }
                if (extensionLogs.length && logs.length) {
                    entries.push({ type: 'separator', label: ( localizeWithPath(_moduleId, 16, "Extension Logs")) });
                }
                for (const { id, label } of extensionLogs) {
                    entries.push({ id, label });
                }
                const entry = await quickInputService.pick(entries, { placeHolder: ( localizeWithPath(_moduleId, 17, "Select Log")) });
                if (entry) {
                    return outputService.showChannel(entry.id);
                }
            }
        }));
    }
    registerOpenLogFileAction() {
        this._register(registerAction2(class extends Action2 {
            constructor() {
                super({
                    id: 'workbench.action.openLogFile',
                    title: ( localize2WithPath(_moduleId, 18, "Open Log File...")),
                    category: Categories.Developer,
                    menu: {
                        id: MenuId.CommandPalette,
                    },
                    metadata: {
                        description: 'workbench.action.openLogFile',
                        args: [{
                                name: 'logFile',
                                schema: {
                                    markdownDescription: ( localizeWithPath(
                                        _moduleId,
                                        19,
                                        "The id of the log file to open, for example `\"window\"`. Currently the best way to get this is to get the ID by checking the `workbench.action.output.show.<id>` commands"
                                    )),
                                    type: 'string'
                                }
                            }]
                    },
                });
            }
            async run(accessor, args) {
                const outputService = accessor.get(IOutputService);
                const quickInputService = accessor.get(IQuickInputService);
                const editorService = accessor.get(IEditorService);
                const fileConfigurationService = accessor.get(IFilesConfigurationService);
                let entry;
                const argName = args && typeof args === 'string' ? args : undefined;
                const extensionChannels = [];
                const coreChannels = [];
                for (const c of outputService.getChannelDescriptors()) {
                    if (c.file && c.log) {
                        const e = { id: c.id, label: c.label, channel: c };
                        if (c.extensionId) {
                            extensionChannels.push(e);
                        }
                        else {
                            coreChannels.push(e);
                        }
                        if (e.id === argName) {
                            entry = e;
                        }
                    }
                }
                if (!entry) {
                    const entries = [...extensionChannels.sort((a, b) => a.label.localeCompare(b.label))];
                    if (entries.length && coreChannels.length) {
                        entries.push({ type: 'separator' });
                        entries.push(...coreChannels.sort((a, b) => a.label.localeCompare(b.label)));
                    }
                    entry = await quickInputService.pick(entries, { placeHolder: ( localizeWithPath(_moduleId, 20, "Select Log File")) });
                }
                if (entry) {
                    const resource = assertIsDefined(entry.channel.file);
                    await fileConfigurationService.updateReadonly(resource, true);
                    await editorService.openEditor({
                        resource,
                        options: {
                            pinned: true,
                        }
                    });
                }
            }
        }));
    }
};
OutputContribution = ( (__decorate([
    ( (__param(0, IOutputService))),
    ( (__param(1, IEditorService))),
    ( (__param(2, IFilesConfigurationService)))
], OutputContribution)));
( (Registry.as(Extensions$2.Workbench))).registerWorkbenchContribution(OutputContribution, 3 );
( (Registry.as(Extensions$3.Configuration))).registerConfiguration({
    id: 'output',
    order: 30,
    title: ( localizeWithPath(_moduleId, 1, "Output")),
    type: 'object',
    properties: {
        'output.smartScroll.enabled': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                21,
                "Enable/disable the ability of smart scrolling in the output view. Smart scrolling allows you to lock scrolling automatically when you click in the output view and unlocks when you click in the last line."
            )),
            default: true,
            scope: 3 ,
            tags: ['output']
        }
    }
});
